import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { API_URL } from '../../config';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Plus, Trash2, Pencil, ExternalLink, PlaySquare,
  Image as ImageIcon, MapPin, Video, X, Film, Instagram
} from 'lucide-react';
import { toast } from 'sonner';

// ── helpers ──────────────────────────────────────────────
function detectPlatform(url) {
  if (!url) return 'other';
  const u = url.toLowerCase();
  if (u.includes('youtube.com') || u.includes('youtu.be')) return 'youtube';
  if (u.includes('instagram.com')) return 'instagram';
  if (u.includes('facebook.com') || u.includes('fb.watch')) return 'facebook';
  return 'other';
}

const platformColors = {
  youtube: 'bg-red-500/20 text-red-400',
  instagram: 'bg-pink-500/20 text-pink-400',
  facebook: 'bg-blue-500/20 text-blue-400',
  other: 'bg-neutral-700/40 text-neutral-400',
};

const platformLabels = {
  youtube: 'YouTube',
  instagram: 'Instagram',
  facebook: 'Facebook',
  other: 'Other',
};

const EMPTY_FORM = {
  title: '',
  description: '',
  status: 'ongoing',
  thumbnail_url: '',
  location: '',
  video_url: '',
  video_urls: [],      // [{url, platform, title}]
  gallery_images: [],  // [string]
};

const FILTER_TABS = ['all', 'ongoing', 'completed'];

// ── component ────────────────────────────────────────────
export default function ProjectsManager() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [activeFilter, setActiveFilter] = useState('all');
  const [formData, setFormData] = useState({ ...EMPTY_FORM });
  const [submitting, setSubmitting] = useState(false);

  // ── fetch ──
  const fetchProjects = async (status) => {
    try {
      const params = status && status !== 'all' ? { status } : {};
      const res = await axios.get(`${API_URL}/projects`, { params });
      setProjects(res.data);
    } catch {
      toast.error('Failed to load projects');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProjects(activeFilter);
  }, [activeFilter]);

  // ── filtered list (client-side fallback) ──
  const filtered = useMemo(() => {
    if (activeFilter === 'all') return projects;
    return projects.filter((p) => p.status === activeFilter);
  }, [projects, activeFilter]);

  // ── modal helpers ──
  const openAddModal = () => {
    setEditingId(null);
    setFormData({ ...EMPTY_FORM });
    setIsModalOpen(true);
  };

  const openEditModal = (proj) => {
    setEditingId(proj.id);
    setFormData({
      title: proj.title || '',
      description: proj.description || '',
      status: proj.status || 'ongoing',
      thumbnail_url: proj.thumbnail_url || '',
      location: proj.location || '',
      video_url: proj.video_url || '',
      video_urls: Array.isArray(proj.video_urls) ? proj.video_urls.map((v) => ({ ...v })) : [],
      gallery_images: Array.isArray(proj.gallery_images) ? [...proj.gallery_images] : [],
    });
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingId(null);
  };

  // ── video_urls helpers ──
  const addVideoEntry = () =>
    setFormData((f) => ({
      ...f,
      video_urls: [...f.video_urls, { url: '', platform: 'other', title: '' }],
    }));

  const updateVideoEntry = (idx, field, value) =>
    setFormData((f) => {
      const list = [...f.video_urls];
      list[idx] = { ...list[idx], [field]: value };
      if (field === 'url') list[idx].platform = detectPlatform(value);
      return { ...f, video_urls: list };
    });

  const removeVideoEntry = (idx) =>
    setFormData((f) => ({
      ...f,
      video_urls: f.video_urls.filter((_, i) => i !== idx),
    }));

  // ── gallery_images helpers ──
  const addGalleryImage = () =>
    setFormData((f) => ({
      ...f,
      gallery_images: [...f.gallery_images, ''],
    }));

  const updateGalleryImage = (idx, value) =>
    setFormData((f) => {
      const list = [...f.gallery_images];
      list[idx] = value;
      return { ...f, gallery_images: list };
    });

  const removeGalleryImage = (idx) =>
    setFormData((f) => ({
      ...f,
      gallery_images: f.gallery_images.filter((_, i) => i !== idx),
    }));

  // ── submit ──
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = { ...formData };
      // strip empty video entries
      payload.video_urls = payload.video_urls.filter((v) => v.url.trim());
      payload.gallery_images = payload.gallery_images.filter((u) => u.trim());

      if (editingId) {
        await axios.put(`${API_URL}/projects/${editingId}`, payload);
        toast.success('Project updated successfully!');
      } else {
        await axios.post(`${API_URL}/projects`, payload);
        toast.success('Project added successfully!');
      }
      closeModal();
      fetchProjects(activeFilter);
    } catch {
      toast.error(editingId ? 'Failed to update project' : 'Failed to add project');
    } finally {
      setSubmitting(false);
    }
  };

  // ── delete ──
  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this project?')) return;
    try {
      await axios.delete(`${API_URL}/projects/${id}`);
      toast.success('Project deleted');
      fetchProjects(activeFilter);
    } catch {
      toast.error('Failed to delete project');
    }
  };

  // ── input classes (reuse) ──
  const inputClass =
    'w-full bg-neutral-950 border border-neutral-800 rounded-xl px-4 py-2.5 text-white placeholder-neutral-600 focus:outline-none focus:border-amber-500 transition';

  // ── render ─────────────────────────────────────────────
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold text-white">Projects Manager</h2>
          <p className="text-neutral-400 text-sm">Manage your ongoing and completed projects.</p>
        </div>
        <button
          onClick={openAddModal}
          className="bg-amber-500 hover:bg-amber-600 text-black font-medium px-4 py-2 rounded-xl flex items-center gap-2 transition"
        >
          <Plus size={20} /> Add Project
        </button>
      </div>

      {/* Filter Tabs */}
      <div className="flex gap-2">
        {FILTER_TABS.map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveFilter(tab)}
            className={`px-4 py-1.5 rounded-lg text-sm font-medium capitalize transition ${
              activeFilter === tab
                ? 'bg-amber-500 text-black'
                : 'bg-neutral-800/60 text-neutral-400 hover:text-white hover:bg-neutral-800'
            }`}
          >
            {tab}
          </button>
        ))}
      </div>

      {/* Grid */}
      {loading ? (
        <div className="flex justify-center py-20">
          <div className="w-8 h-8 border-2 border-amber-500 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <AnimatePresence>
            {filtered.map((proj) => {
              const videoCount = Array.isArray(proj.video_urls) ? proj.video_urls.length : proj.video_url ? 1 : 0;
              const imgCount = Array.isArray(proj.gallery_images) ? proj.gallery_images.length : 0;

              return (
                <motion.div
                  key={proj.id}
                  layout
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  className="bg-neutral-900 border border-neutral-800 rounded-2xl overflow-hidden group"
                >
                  {/* Thumbnail */}
                  <div className="aspect-video bg-neutral-800 relative">
                    {proj.thumbnail_url ? (
                      <img src={proj.thumbnail_url} alt={proj.title} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <ImageIcon size={40} className="text-neutral-600" />
                      </div>
                    )}
                    {/* Status badge */}
                    <span
                      className={`absolute top-2 right-2 text-xs font-semibold px-2 py-1 rounded-md ${
                        proj.status === 'completed'
                          ? 'bg-green-500/20 text-green-400'
                          : 'bg-blue-500/20 text-blue-400'
                      }`}
                    >
                      {(proj.status || '').toUpperCase()}
                    </span>
                  </div>

                  {/* Body */}
                  <div className="p-5">
                    <h3 className="text-lg font-bold text-white mb-1 truncate">{proj.title}</h3>
                    <p className="text-sm text-neutral-400 line-clamp-2 mb-3">
                      {proj.description || 'No description provided.'}
                    </p>

                    {/* Meta row */}
                    <div className="flex flex-wrap items-center gap-2 mb-4">
                      {proj.location && (
                        <span className="inline-flex items-center gap-1 text-xs text-neutral-400 bg-neutral-800 px-2 py-1 rounded-md">
                          <MapPin size={12} /> {proj.location}
                        </span>
                      )}
                      {videoCount > 0 && (
                        <span className="inline-flex items-center gap-1 text-xs text-amber-400 bg-amber-500/10 px-2 py-1 rounded-md">
                          <Video size={12} /> {videoCount} video{videoCount > 1 ? 's' : ''}
                        </span>
                      )}
                      {imgCount > 0 && (
                        <span className="inline-flex items-center gap-1 text-xs text-purple-400 bg-purple-500/10 px-2 py-1 rounded-md">
                          <ImageIcon size={12} /> {imgCount} image{imgCount > 1 ? 's' : ''}
                        </span>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="flex items-center justify-between border-t border-neutral-800 pt-4">
                      {proj.video_url ? (
                        <a
                          href={proj.video_url}
                          target="_blank"
                          rel="noreferrer"
                          className="text-amber-500 hover:text-amber-400 text-sm flex items-center gap-1"
                        >
                          View Video <ExternalLink size={14} />
                        </a>
                      ) : (
                        <span className="text-neutral-600 text-sm">No Video</span>
                      )}

                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => openEditModal(proj)}
                          className="text-neutral-500 hover:text-amber-500 transition"
                          title="Edit"
                        >
                          <Pencil size={18} />
                        </button>
                        <button
                          onClick={() => handleDelete(proj.id)}
                          className="text-neutral-500 hover:text-red-500 transition"
                          title="Delete"
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </AnimatePresence>

          {filtered.length === 0 && (
            <div className="col-span-full py-12 text-center text-neutral-500 border border-dashed border-neutral-800 rounded-2xl">
              No projects found. Add one to get started.
            </div>
          )}
        </div>
      )}

      {/* ── Add / Edit Modal ─────────────────────────────── */}
      <AnimatePresence>
        {isModalOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 bg-black/80 backdrop-blur-sm"
              onClick={closeModal}
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="relative bg-neutral-900 border border-neutral-800 rounded-2xl p-6 w-full max-w-lg shadow-2xl max-h-[90vh] overflow-y-auto"
            >
              <h3 className="text-xl font-bold text-white mb-6">
                {editingId ? 'Edit Project' : 'Add New Project'}
              </h3>

              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Title */}
                <div>
                  <label className="block text-sm font-medium text-neutral-400 mb-1">Title *</label>
                  <input
                    required
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    className={inputClass}
                  />
                </div>

                {/* Status */}
                <div>
                  <label className="block text-sm font-medium text-neutral-400 mb-1">Status</label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                    className={inputClass}
                  >
                    <option value="ongoing">Ongoing</option>
                    <option value="completed">Completed</option>
                  </select>
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-medium text-neutral-400 mb-1">Description</label>
                  <textarea
                    rows={3}
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className={inputClass}
                  />
                </div>

                {/* Location */}
                <div>
                  <label className="block text-sm font-medium text-neutral-400 mb-1">Location</label>
                  <input
                    type="text"
                    value={formData.location}
                    onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                    placeholder="e.g. Downtown Dubai"
                    className={inputClass}
                  />
                </div>

                {/* Thumbnail URL */}
                <div>
                  <label className="block text-sm font-medium text-neutral-400 mb-1">Thumbnail URL</label>
                  <input
                    type="url"
                    value={formData.thumbnail_url}
                    onChange={(e) => setFormData({ ...formData, thumbnail_url: e.target.value })}
                    placeholder="https://..."
                    className={inputClass}
                  />
                </div>

                {/* Legacy single video URL */}
                <div>
                  <label className="block text-sm font-medium text-neutral-400 mb-1">Primary Video URL</label>
                  <input
                    type="url"
                    value={formData.video_url}
                    onChange={(e) => setFormData({ ...formData, video_url: e.target.value })}
                    placeholder="https://..."
                    className={inputClass}
                  />
                </div>

                {/* ── Multiple Video URLs ─────────────── */}
                <div>
                  <label className="block text-sm font-medium text-neutral-400 mb-2">Video URLs</label>
                  <div className="space-y-3">
                    {formData.video_urls.map((entry, idx) => {
                      const plat = entry.platform || detectPlatform(entry.url);
                      return (
                        <div key={idx} className="flex items-start gap-2">
                          <div className="flex-1 space-y-1.5">
                            <div className="flex items-center gap-2">
                              <input
                                type="url"
                                value={entry.url}
                                onChange={(e) => updateVideoEntry(idx, 'url', e.target.value)}
                                placeholder="https://..."
                                className={inputClass}
                              />
                              <span
                                className={`whitespace-nowrap text-[10px] font-semibold px-2 py-1 rounded-md ${platformColors[plat]}`}
                              >
                                {platformLabels[plat]}
                              </span>
                            </div>
                            <input
                              type="text"
                              value={entry.title || ''}
                              onChange={(e) => updateVideoEntry(idx, 'title', e.target.value)}
                              placeholder="Video title (optional)"
                              className={`${inputClass} text-sm py-1.5`}
                            />
                          </div>
                          <button
                            type="button"
                            onClick={() => removeVideoEntry(idx)}
                            className="mt-2 p-1.5 rounded-lg text-neutral-500 hover:text-red-400 hover:bg-red-500/10 transition"
                          >
                            <X size={16} />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                  <button
                    type="button"
                    onClick={addVideoEntry}
                    className="mt-2 text-sm text-amber-500 hover:text-amber-400 flex items-center gap-1 transition"
                  >
                    <Plus size={14} /> Add Video
                  </button>
                </div>

                {/* ── Gallery Images ──────────────────── */}
                <div>
                  <label className="block text-sm font-medium text-neutral-400 mb-2">Gallery Images</label>
                  <div className="space-y-2">
                    {formData.gallery_images.map((url, idx) => (
                      <div key={idx} className="flex items-center gap-2">
                        <input
                          type="url"
                          value={url}
                          onChange={(e) => updateGalleryImage(idx, e.target.value)}
                          placeholder="https://..."
                          className={inputClass}
                        />
                        <button
                          type="button"
                          onClick={() => removeGalleryImage(idx)}
                          className="p-1.5 rounded-lg text-neutral-500 hover:text-red-400 hover:bg-red-500/10 transition"
                        >
                          <X size={16} />
                        </button>
                      </div>
                    ))}
                  </div>
                  <button
                    type="button"
                    onClick={addGalleryImage}
                    className="mt-2 text-sm text-amber-500 hover:text-amber-400 flex items-center gap-1 transition"
                  >
                    <Plus size={14} /> Add Image
                  </button>
                </div>

                {/* Actions */}
                <div className="pt-4 flex gap-3">
                  <button
                    type="button"
                    onClick={closeModal}
                    className="flex-1 px-4 py-2.5 rounded-xl border border-neutral-700 text-neutral-300 hover:bg-neutral-800 transition"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="flex-1 px-4 py-2.5 rounded-xl bg-amber-500 text-black font-medium hover:bg-amber-600 transition disabled:opacity-50"
                  >
                    {submitting ? 'Saving…' : editingId ? 'Update Project' : 'Save Project'}
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}
